import type { Map as MapLibreMap } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import type { TerraDraw } from 'terra-draw';
import { useColorMode } from '../../../../../shared/features/theme/components/ColorModeProvider';
import { createZoneDraw } from '../adapters/create-zone-draw';
import {
  initialDrawMode,
  shapesReplacedBy,
  zoneDrawFeatures,
  zoneGeometryFromSnapshot,
  type DrawnFeature,
  type ZoneDrawMode,
  type ZoneGeometry,
} from '../policies/zone-draw-features';
import { trackMapRemoval } from '../adapters/track-map-removal';

interface UseZoneDrawingArgs {
  /** The loaded map, or null until it is. */
  map: MapLibreMap | null;
  /** The zone being edited (`new` for a new one), or null when no panel is open. A new key starts a fresh drawing. */
  drawingKey: string | null;
  /** Read once when a drawing starts: the shape to load for editing. */
  initialValue: () => ZoneGeometry;
  onChange: (value: ZoneGeometry) => void;
}

function snapshotOf(draw: TerraDraw): DrawnFeature[] {
  return draw.getSnapshot() as DrawnFeature[];
}

/** Puts a shape on the map ready to edit (or, with none, ready to draw a polygon). Loaded features aren't edits, so nothing is emitted. */
function loadInto(draw: TerraDraw, geometry: ZoneGeometry): ZoneDrawMode {
  const [loaded] = draw.addFeatures(zoneDrawFeatures(geometry) as Parameters<TerraDraw['addFeatures']>[0]);
  const mode = initialDrawMode(geometry);
  draw.setMode(mode);
  if (loaded?.valid && loaded.id !== undefined) draw.selectFeature(loaded.id);
  return mode;
}

/** Loads the zone's shape and wires terra-draw's events to the form. */
function startDrawing(draw: TerraDraw, initial: ZoneGeometry, emit: () => void, onModeChange: (mode: ZoneDrawMode) => void) {
  draw.start();
  onModeChange(loadInto(draw, initial));

  draw.on('finish', (id, context) => {
    if (context.mode === 'polygon' || context.mode === 'point') {
      draw.removeFeatures(shapesReplacedBy(snapshotOf(draw), id));
      draw.setMode('select');
      draw.selectFeature(id);
      onModeChange('select');
    }
    emit();
  });
  draw.on('change', (_ids, _type, context) => {
    // Loading the saved shape isn't an edit; only the admin's own changes reach the form.
    if (context && 'origin' in context && context.origin === 'api') return;
    emit();
  });
}

/**
 * terra-draw on the Zones map for the zone whose panel is open. The map is shared with every other
 * zone; drawing only runs while `drawingKey` is set, so a closed panel leaves the map read-only.
 */
export function useZoneDrawing({ map, drawingKey, initialValue, onChange }: UseZoneDrawingArgs) {
  const drawRef = useRef<TerraDraw | null>(null);
  const callbacksRef = useRef({ initialValue, onChange });
  const [drawMode, setDrawModeState] = useState<ZoneDrawMode | null>(null);
  const { mode } = useColorMode();

  useEffect(() => {
    callbacksRef.current = { initialValue, onChange };
  });

  useEffect(() => {
    if (!map || !drawingKey) return;
    const draw = createZoneDraw(map, mode);
    drawRef.current = draw;
    const emit = () => callbacksRef.current.onChange(zoneGeometryFromSnapshot(snapshotOf(draw)));
    startDrawing(draw, callbacksRef.current.initialValue(), emit, setDrawModeState);

    const removal = trackMapRemoval(map);

    return () => {
      drawRef.current = null;
      setDrawModeState(null);
      // terra-draw's stop() fails on a removed map, whose teardown has taken terra-draw's layers with it anyway.
      if (removal.isRemoved()) return;
      removal.dispose();
      draw.stop();
    };
  }, [map, drawingKey, mode]);

  function setDrawMode(mode: ZoneDrawMode) {
    const draw = drawRef.current;
    if (!draw) return;
    draw.setMode(mode);
    setDrawModeState(mode);
  }

  /** Replaces whatever is drawn with `geometry`, e.g. a place picked by name; the caller sets the form's shape itself. */
  function loadShape(geometry: ZoneGeometry) {
    const draw = drawRef.current;
    if (!draw) return;
    draw.clear();
    setDrawModeState(loadInto(draw, geometry));
  }

  /** Removes the shape; the zone can be saved unplaced, or a new shape drawn. */
  function clearShape() {
    const draw = drawRef.current;
    if (!draw) return;
    draw.clear();
    setDrawMode('polygon');
    callbacksRef.current.onChange({ polygon: null, centroid: null });
  }

  return { isDrawing: drawMode !== null, drawMode, setDrawMode, loadShape, clearShape };
}
